export type EventRosterApiRow = {
  rosterKey?: string;
  registrationId?: number | null;
  rowKind?: "member" | "purchaser" | "guest";
  hasMemberAccount?: boolean;
  purchaseGroupId?: string | null;
  purchaserName?: string | null;
  isTicketBacked?: boolean;
  registrationState: string;
  reviewVersion: number;
  fullName: string;
  jobTitle?: string | null;
  email: string;
  company?: string | null;
  registeredAt: string | Date | null;
  photoUrl?: string | null;
  memberPhotoUrl?: string | null;
};

export type EventAttendeeStatus =
  | "confirmed"
  | "waitlist"
  | "review"
  | "cancelled"
  | "checkout_pending"
  | "checkout_expired"
  | "checkout_cancelled"
  | "payment_failed"
  | "ticket_review";

export type EventAttendeeRow = {
  id: string;
  rosterKey: string;
  registrationId: number | null;
  rowKind: "member" | "purchaser" | "guest";
  hasMemberAccount: boolean;
  purchaseGroupId: string | null;
  purchaserName: string | null;
  isTicketBacked: boolean;
  registrationState: string;
  reviewVersion: number;
  name: string;
  title: string;
  email: string;
  company: string;
  registeredAt: string;
  status: EventAttendeeStatus;
  photoUrl: string | null;
  memberPhotoUrl: string | null;
};

export function rosterStatusFor(registrationState: string): EventAttendeeStatus {
  if (registrationState === "registered") return "confirmed";
  if (registrationState === "waitlisted") return "waitlist";
  if (registrationState === "pending_review") return "review";
  if (registrationState === "ticket_review") return "ticket_review";
  if (registrationState === "checkout_pending" || registrationState === "payment_pending") return "checkout_pending";
  if (registrationState === "checkout_expired") return "checkout_expired";
  if (registrationState === "checkout_cancelled") return "checkout_cancelled";
  if (registrationState === "payment_failed") return "payment_failed";
  if (registrationState === "cancelled" || registrationState === "rejected") return "cancelled";
  // Unknown future states are never silently reported as an actual waitlist.
  return "ticket_review";
}

export function mapEventRosterRows(
  rows: EventRosterApiRow[],
  pendingIdByEmail: ReadonlyMap<string, number> = new Map(),
): EventAttendeeRow[] {
  return rows.map((row, index) => {
    const rosterKey = row.rosterKey ?? `${row.email}-${index}`;
    const rowKind = row.rowKind ?? "member";
    return {
      id: rosterKey,
      rosterKey,
      registrationId: row.registrationId === undefined ? pendingIdByEmail.get(row.email.trim().toLowerCase()) ?? null : row.registrationId,
      rowKind,
      hasMemberAccount: row.hasMemberAccount ?? rowKind === "member",
      purchaseGroupId: row.purchaseGroupId ?? null,
      purchaserName: row.purchaserName ?? null,
      isTicketBacked: row.isTicketBacked ?? false,
      registrationState: row.registrationState,
      reviewVersion: row.reviewVersion,
      name: row.fullName,
      title: row.jobTitle ?? "",
      email: row.email,
      company: row.company ?? "",
      registeredAt: row.registeredAt ? new Date(row.registeredAt).toISOString().split("T")[0] : "—",
      status: rosterStatusFor(row.registrationState),
      photoUrl: row.photoUrl ?? null,
      memberPhotoUrl: row.memberPhotoUrl ?? null,
    };
  });
}

/**
 * A purchaser's name is useful search context for their guest seats. When the
 * purchaser matches, keep every row from that ticket order visible together.
 */
export function filterEventRosterRows(rows: EventAttendeeRow[], search: string): EventAttendeeRow[] {
  const query = search.trim().toLowerCase();
  if (!query) return rows;
  const matchingGroups = new Set(
    rows.filter(row => row.purchaseGroupId && row.purchaserName?.toLowerCase().includes(query))
      .map(row => row.purchaseGroupId as string),
  );
  return rows.filter(row =>
    row.name.toLowerCase().includes(query) ||
    row.email.toLowerCase().includes(query) ||
    row.company.toLowerCase().includes(query) ||
    Boolean(row.purchaserName?.toLowerCase().includes(query)) ||
    Boolean(row.purchaseGroupId && matchingGroups.has(row.purchaseGroupId)),
  );
}

export function canShowMemberProfileAction(row: EventAttendeeRow): boolean {
  return row.rowKind !== "guest" && row.hasMemberAccount;
}

export function canManuallyReviewEventRosterRow(row: EventAttendeeRow): boolean {
  const checkoutOnlyStates = new Set([
    "checkout_pending",
    "checkout_expired",
    "checkout_cancelled",
    "payment_pending",
    "payment_failed",
  ]);
  return row.registrationState === "pending_review" &&
    row.registrationId !== null &&
    !row.isTicketBacked &&
    !checkoutOnlyStates.has(row.registrationState);
}
